import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const RecentActivities = ({ activities = [] }) => {
  const navigate = useNavigate();

  // Get latest 5 activities
  const recentActivities = activities
    ?.sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt))
    ?.slice(0, 5);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'call': return 'Phone';
      case 'email': return 'Mail';
      case 'meeting': return 'Calendar';
      case 'task': return 'CheckSquare';
      case 'deal_created': case'deal_updated': case'deal_won': case'deal_lost':
        return 'Target';
      case 'note': return 'FileText';
      default: return 'Activity';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'pending': return 'text-warning';
      case 'cancelled': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString();
  };

  return (
    <motion.div
      className="bg-card border border-border rounded-xl p-6 shadow-elevation-1"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Recent Activities</h3>
          <p className="text-sm text-muted-foreground">Latest updates from your CRM</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/activities')}>
          <Icon name="Plus" size={16} className="mr-2" />
          Add Activity
        </Button>
      </div>
      <div className="space-y-4">
        {recentActivities?.length > 0 ? (
          recentActivities?.map((activity, index) => (
            <motion.div
              key={activity?.id}
              className="flex items-start space-x-4 p-4 rounded-lg hover:bg-muted/50 transition-smooth cursor-pointer"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              onClick={() => navigate('/activities')}
            >
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon
                    name={getActivityIcon(activity?.activityType)}
                    size={18}
                    className="text-primary"
                  />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-card-foreground mb-1">
                      {activity?.title}
                    </h4>
                    <div className="flex items-center space-x-3">
                      <span className={`text-xs font-medium ${getStatusColor(activity?.status)}`}>
                        {activity?.status || 'pending'}
                      </span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">
                        {formatTimestamp(activity?.createdAt)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0 ml-4">
                    <Button variant="ghost" size="icon" className="w-8 h-8">
                      <Icon name="MoreHorizontal" size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Activity" size={48} className="mx-auto mb-2 opacity-50" />
            <p>No recent activities</p>
            <p className="text-xs mt-1">Activities will appear here as you work</p>
          </div>
        )}
      </div>
      <div className="mt-6 pt-4 border-t border-border">
        <Button variant="ghost" className="w-full" onClick={() => navigate('/activities')}>
          <Icon name="ArrowRight" size={16} className="mr-2" />
          View All Activities
        </Button>
      </div>
    </motion.div>
  );
};

export default RecentActivities;